import React, { useState, useMemo, useEffect, useCallback } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import BugTrackerFilters from './components/BugTrackerFilters';
import BugIssueRow from './components/BugIssueRow';
import BugIssueForm from './components/BugIssueForm';
import { bugTrackerService } from '../../services/bugTrackerService';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const ApplicationBugTracker = () => {
  const { userProfile } = useAuth();
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ type: 'all', status: 'all', priority: 'all' });
  const [searchQuery, setSearchQuery] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingIssue, setEditingIssue] = useState(null);
  const [saving, setSaving] = useState(false);
  const [allUsers, setAllUsers] = useState([]);

  const loadIssues = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await bugTrackerService?.getAll();
      setIssues(data);
    } catch (err) {
      setError(err?.message || 'Failed to load issues');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadIssues();

    const channel = bugTrackerService?.subscribeToChanges(
      (newIssue) => setIssues(prev => [newIssue, ...prev]),
      (updatedIssue) => setIssues(prev => prev?.map(i => i?.id === updatedIssue?.id ? updatedIssue : i)),
      (deletedId) => setIssues(prev => prev?.filter(i => i?.id !== deletedId))
    );

    // Load all user profiles for the requester dropdown
    supabase?.from('user_profiles')?.select('id, nickname, full_name, email, is_active')?.eq('is_active', true)?.order('nickname')?.then(({ data }) => {
        if (data) setAllUsers(data);
      });

    return () => bugTrackerService?.unsubscribe(channel);
  }, [loadIssues]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const filteredIssues = useMemo(() => {
    return issues?.filter(issue => {
      const matchesSearch = !searchQuery ||
        issue?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        issue?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
      const matchesType = filters?.type === 'all' || issue?.type === filters?.type;
      const matchesStatus = filters?.status === 'all' || issue?.status === filters?.status;
      const matchesPriority = filters?.priority === 'all' || issue?.priority === filters?.priority;
      return matchesSearch && matchesType && matchesStatus && matchesPriority;
    });
  }, [issues, filters, searchQuery]);

  const stats = useMemo(() => ({
    total: issues?.length,
    open: issues?.filter(i => i?.status === 'open')?.length,
    inProgress: issues?.filter(i => i?.status === 'in_progress')?.length,
    resolved: issues?.filter(i => i?.status === 'resolved' || i?.status === 'closed')?.length,
    bugs: issues?.filter(i => i?.type === 'bug')?.length,
    enhancements: issues?.filter(i => i?.type === 'enhancement')?.length,
  }), [issues]);

  const handleSave = async (formData) => {
    try {
      setSaving(true);
      if (editingIssue) {
        await bugTrackerService?.update(editingIssue?.id, formData);
      } else {
        await bugTrackerService?.create(formData);
      }
      setIsFormOpen(false);
      setEditingIssue(null);
      await loadIssues();
    } catch (err) {
      alert('Failed to save issue: ' + (err?.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (issue) => {
    setEditingIssue(issue);
    setIsFormOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this issue?')) return;
    try {
      await bugTrackerService?.remove(id);
    } catch (err) {
      alert('Failed to delete issue: ' + (err?.message || 'Unknown error'));
    }
  };

  const handleToggleComplete = async (id) => {
    const issue = issues?.find(i => i?.id === id);
    if (!issue) return;
    const isComplete = issue?.status === 'resolved' || issue?.status === 'closed';
    try {
      await bugTrackerService?.update(id, { status: isComplete ? 'open' : 'resolved' });
    } catch (err) {
      alert('Failed to update issue: ' + (err?.message || 'Unknown error'));
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await bugTrackerService?.update(id, { status: newStatus });
    } catch (err) {
      alert('Failed to update status: ' + (err?.message || 'Unknown error'));
    }
  };

  const handleNewIssue = () => {
    setEditingIssue(null);
    setIsFormOpen(true);
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-background">
        {/* Page Header */}
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <Icon name="Bug" size={20} className="text-red-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Application Bug Tracker</h1>
                  <p className="text-sm text-muted-foreground">Track bugs and enhancement requests for ConstructionPro</p>
                </div>
              </div>
              <Button onClick={handleNewIssue} iconName="Plus" iconPosition="left">
                New Issue
              </Button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Error Banner */}
          {error && (
            <div className="mb-4 flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <Icon name="AlertCircle" size={16} />
              <span>{error}</span>
              <button onClick={loadIssues} className="ml-auto text-xs underline hover:no-underline">Retry</button>
            </div>
          )}

          {/* Stats Row */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            {[
              { label: 'Total', value: stats?.total, color: 'text-foreground', bg: 'bg-card' },
              { label: 'Open', value: stats?.open, color: 'text-yellow-600', bg: 'bg-yellow-50' },
              { label: 'In Progress', value: stats?.inProgress, color: 'text-blue-600', bg: 'bg-blue-50' },
              { label: 'Resolved', value: stats?.resolved, color: 'text-green-600', bg: 'bg-green-50' },
              { label: 'Bugs', value: stats?.bugs, color: 'text-red-600', bg: 'bg-red-50' },
              { label: 'Enhancements', value: stats?.enhancements, color: 'text-purple-600', bg: 'bg-purple-50' },
            ]?.map(stat => (
              <div key={stat?.label} className={`${stat?.bg} border border-border rounded-lg px-4 py-3 text-center`}>
                <div className={`text-2xl font-bold ${stat?.color}`}>
                  {loading ? <span className="inline-block w-6 h-6 rounded bg-muted animate-pulse" /> : stat?.value}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">{stat?.label}</div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="bg-card border border-border rounded-lg px-4 py-3 mb-4">
            <BugTrackerFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onSearch={setSearchQuery}
              searchQuery={searchQuery}
            />
          </div>

          {/* Issues Table */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <span className="text-sm font-medium text-foreground">
                {loading ? 'Loading...' : `${filteredIssues?.length} issue${filteredIssues?.length !== 1 ? 's' : ''}${filteredIssues?.length !== issues?.length ? ` (filtered from ${issues?.length})` : ''}`}
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-muted/50 border-b border-border">
                    <th className="px-4 py-2.5 w-10"></th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Title</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Type</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Priority</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Due Date</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Requester</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-12 text-center">
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          <p className="text-sm text-muted-foreground">Loading issues...</p>
                        </div>
                      </td>
                    </tr>
                  ) : filteredIssues?.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-12 text-center">
                        <div className="flex flex-col items-center gap-2">
                          <Icon name="SearchX" size={32} className="text-muted-foreground/40" />
                          <p className="text-sm text-muted-foreground">No issues match your filters</p>
                          <button
                            onClick={() => { setFilters({ type: 'all', status: 'all', priority: 'all' }); setSearchQuery(''); }}
                            className="text-xs text-primary hover:underline mt-1"
                          >
                            Clear filters
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredIssues?.map(issue => (
                      <BugIssueRow
                        key={issue?.id}
                        issue={issue}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onToggleComplete={handleToggleComplete}
                        onStatusChange={handleStatusChange}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <BugIssueForm
          isOpen={isFormOpen}
          onClose={() => { setIsFormOpen(false); setEditingIssue(null); }}
          onSave={handleSave}
          editingIssue={editingIssue}
          saving={saving}
          requesterNickname={userProfile?.nickname || userProfile?.full_name || userProfile?.email || ''}
          allUsers={allUsers}
        />
      </div>
    </MainLayout>
  );
};

export default ApplicationBugTracker;
